import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { storeReplacementThreshold } from "../../../Store/TemplateStore/template.action";

import InputBox from "../../../components/InputBox";
import { TextFieldValidator } from "../../../components/InputBox/TextFieldValidator";

import { REPLACEMENT_THRESHOLD } from "./replacementThreshold.config";

import "./index.scss";

const ReplacementThreshold = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const {
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
    flow,
    pressure,
    permeate_quality,
  } = useSelector((state) => state);

  const unitMapping = {
    max_feed_pressure: pressure,
    max_permeate_quality: permeate_quality,
    startup_pressure_drop: pressure,
    designed_permeate_flow: flow,
    max_pressure_drop: pressure,
    min_permeate_flow: flow,
  };

  const [defaultThresholdState, setDefaultThresholdState] = useState({
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
  });

  const [errors, setErrors] = useState({
    max_feed_pressure: null,
    max_permeate_quality: null,
    std_warranty_period: null,
    startup_pressure_drop: null,
    designed_permeate_flow: null,
    std_membrane_age: null,
    max_pressure_drop: null,
    min_permeate_flow: null,
  });
  const [noError, setNoError] = useState(true);

  const [thresholdFields, setThresholdFields] = useState(REPLACEMENT_THRESHOLD);

  useEffect(() => {
    const noError = Object.values(errors).every(
      (error) => error === "" || error === null
    );
    setNoError(noError);
    props.setNoThresholdError(noError);
  }, [errors]);

  useEffect(() => {
    let allMandatoryFieldsFilled = true;
    Object.values(thresholdFields).forEach((field) => {
      if (field.mandatory) {
        const key = field.key;
        const hasValue = defaultThresholdState[key];
        if (hasValue === null || hasValue === undefined || hasValue === "") {
          const fieldName = t(`replacementThresholdStrings.${key}`).split(
            "("
          )[0];
          const a = { ...errors };
          setErrors({
            ...errors,
            [key]: `${fieldName} ${t(
              "defaultInformationStrings.shouldgreater"
            )} ${thresholdFields[key].minValue}`,
          });
          allMandatoryFieldsFilled = false;
        }
      }
    });
    props.setMandatoryThresholdFilled(allMandatoryFieldsFilled);
  }, [defaultThresholdState, props.isNextClicked]);

  useEffect(() => {
    setDefaultThresholdState({
      ...defaultThresholdState,
      max_pressure_drop: defaultThresholdState.startup_pressure_drop * 2,
    });
    setErrors({ ...errors, max_pressure_drop: "" });
  }, [defaultThresholdState.startup_pressure_drop]);

  useEffect(() => {
    if (defaultThresholdState.designed_permeate_flow) {
      const value =
        Math.round(
          (defaultThresholdState.designed_permeate_flow / 100) * 60 * 100
        ) / 100;
      setDefaultThresholdState({
        ...defaultThresholdState,
        min_permeate_flow: value,
      });
      setErrors({ ...errors, min_permeate_flow: "" });
    }
  }, [defaultThresholdState.designed_permeate_flow]);

  const dispatch = useDispatch();

  const handleInputChange = (field, value, minValue, maxValue) => {
    setDefaultThresholdState({ ...defaultThresholdState, [field]: value });
    setErrors({ ...errors, [field]: null });
    const fieldName = t(`replacementThresholdStrings.${field}`).split("(")[0];

    if ((value === "" && minValue !== null) || parseFloat(value) <= minValue) {
      setErrors({
        ...errors,
        [field]: `${fieldName} ${t(
          "defaultInformationStrings.shouldgreater"
        )} ${minValue}`,
      });
    } else if (
      maxValue !== null &&
      (parseFloat(value) < minValue || parseFloat(value) > maxValue)
    ) {
      setErrors({
        ...errors,
        [field]: `${fieldName}  ${t(
          "defaultInformationStrings.cannotLess"
        )} ${minValue}  ${t(
          "defaultInformationStrings.greaterThan"
        )} ${maxValue}`,
      });
    } else {
      dispatch(storeReplacementThreshold({ [field]: value }));
    }
  };

  const replacementThreshold = () => {
    const permeateFlow = defaultThresholdState.designed_permeate_flow;
    if (noError && permeateFlow) {
      const thresholdValues = {
        max_feed_pressure: defaultThresholdState.max_feed_pressure,
        max_permeate_quality: defaultThresholdState.max_permeate_quality,
        std_warranty_period: defaultThresholdState.std_warranty_period,
        startup_pressure_drop: defaultThresholdState.startup_pressure_drop,
        designed_permeate_flow: defaultThresholdState.designed_permeate_flow,
        std_membrane_age: defaultThresholdState.std_membrane_age,
        max_pressure_drop: defaultThresholdState.max_pressure_drop,
        min_permeate_flow: defaultThresholdState.min_permeate_flow,
      };
      dispatch(storeReplacementThreshold(thresholdValues));
    } else {
      props.setActiveStep(0);
    }
  };
  React.useImperativeHandle(ref, () => ({ replacementThreshold }));

  const replaceUnit = (value, key) => {
    const a = unitMapping[key];
    return value.replace("<unit>", unitMapping[key]);
  };

  return (
    <div className="lightGrayBorder section-container">
      <div className="headerTealText">
        {t("replacementThresholdStrings.replacementThresholds")}
      </div>
      <div className="grid-container-threshold ">
        {Object.keys(thresholdFields).map((key, index) => (
          <div className="grid-item-threshold " key={index}>
            <InputBox
              value={defaultThresholdState[key] || ""}
              name={key}
              labelText={replaceUnit(
                t(`replacementThresholdStrings.${key}`),
                key
              )}
              onChange={(value) =>
                handleInputChange(
                  key,
                  value,
                  thresholdFields[key].minValue,
                  thresholdFields[key].maxValue
                )
              }
              validator={TextFieldValidator.NUMBER_TWO_DECIMAL}
              errorMsg={errors[key]}
              maxLength={10}
              isRequired={thresholdFields[key].mandatory}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

export default ReplacementThreshold;
